import { useRef } from "react";

type DefaultFactory<TProps, TValue> = (rawProps: TProps) => TValue;
type DefaultSpec<TProps, TValue> =
  | TValue
  | { value: TValue }
  | { factory: DefaultFactory<TProps, TValue> | TValue };

type DefaultMap<TProps extends Record<string, unknown>> = Partial<{
  [K in keyof TProps]: DefaultSpec<TProps, TProps[K]>;
}>;
type VuePropsRef<TProps> = { current: TProps };

function hasOwn(obj: object, key: PropertyKey): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function resolveDefaultSpec<TProps, TValue>(
  spec: DefaultSpec<TProps, TValue>,
  rawProps: TProps
): TValue {
  if (spec && typeof spec === "object") {
    if (hasOwn(spec, "factory")) {
      const factory = (spec as { factory: DefaultFactory<TProps, TValue> | TValue }).factory;
      return typeof factory === "function"
        ? (factory as DefaultFactory<TProps, TValue>)(rawProps)
        : factory;
    }
    if (hasOwn(spec, "value")) return (spec as { value: TValue }).value;
  }
  return spec as TValue;
}

/**
 * Explicit marker for defaults that must be evaluated once per component
 * instance when prop is absent (value === undefined).
 */
export function vueDefaultFactory<TProps, TValue>(
  factory: DefaultFactory<TProps, TValue>
): { factory: DefaultFactory<TProps, TValue> } {
  return { factory };
}

/**
 * Explicit marker for literal defaults (including function-valued props).
 */
export function vueDefaultValue<TValue>(value: TValue): { value: TValue } {
  return { value };
}

/**
 * Vue-like props adapter for migrated React components.
 *
 * - Keeps latest resolved props in props.current (closure-safe reads).
 * - Applies defaults only when raw prop is undefined.
 * - Caches default results per prop key for the component instance.
 * - Never mutates rawProps.
 */
export function useVueProps<TProps extends Record<string, unknown>>(
  rawProps: TProps,
  getDefaultProps?: (rawProps: TProps) => DefaultMap<TProps> | null | undefined
): VuePropsRef<TProps> {
  const propsRef = useRef(rawProps);
  const defaultsCacheRef = useRef<Partial<Record<keyof TProps, unknown>>>(Object.create(null));

  let resolved = rawProps;
  const defaultsSpec =
    typeof getDefaultProps === "function" ? (getDefaultProps(rawProps) ?? null) : null;

  if (defaultsSpec && typeof defaultsSpec === "object") {
    for (const key of Object.keys(defaultsSpec) as Array<keyof TProps>) {
      if (rawProps[key] !== undefined) continue;

      if (resolved === rawProps) {
        resolved = { ...rawProps };
      }

      if (!hasOwn(defaultsCacheRef.current, key)) {
        defaultsCacheRef.current[key] = resolveDefaultSpec(
          defaultsSpec[key] as DefaultSpec<TProps, TProps[typeof key]>,
          rawProps
        );
      }

      resolved[key] = defaultsCacheRef.current[key] as TProps[typeof key];
    }
  }

  propsRef.current = resolved;
  return propsRef;
}
